import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Heart, 
  Gift, 
  CreditCard,
  Smartphone,
  FileText,
  HelpCircle,
  Send,
  Copy,
  Check
} from 'lucide-react';

const Dizimos = () => {
  const [pixCopiado, setPixCopiado] = useState('');

  const copiarPix = (texto, tipo) => {
    navigator.clipboard.writeText(texto);
    setPixCopiado(tipo);
    setTimeout(() => setPixCopiado(''), 3000);
  };

  const versiculos = [
    {
      texto: "Trazei todos os dízimos à casa do tesouro, para que haja mantimento na minha casa; e provai-me nisto, diz o Senhor dos Exércitos, se eu não vos abrir as janelas do céu e não derramar sobre vós uma bênção tal, que dela vos advenha a maior abastança.",
      referencia: "Malaquias 3:10"
    },
    {
      texto: "Cada um contribua segundo propôs no seu coração; não com tristeza, ou por necessidade; porque Deus ama ao que dá com alegria.",
      referencia: "2 Coríntios 9:7"
    }
  ];

  const investimentos = [
    { icone: '🏠', titulo: 'Manutenção e infraestrutura da igreja' },
    { icone: '❤️', titulo: 'Projetos sociais e evangelísticos' },
    { icone: '🌍', titulo: 'Apoio a missionários' },
    { icone: '🎉', titulo: 'Eventos e programações da igreja' },
    { icone: '📚', titulo: 'Materiais para ministérios' }
  ];

  const perguntas = [
    {
      pergunta: 'Qual a diferença entre dízimo e oferta?',
      resposta: 'O dízimo corresponde a 10% de nossa renda, que devolvemos ao Senhor como reconhecimento de que tudo vem dele. Já a oferta é uma contribuição voluntária, acima do dízimo, conforme cada um propõe em seu coração.'
    },
    {
      pergunta: 'Como posso contribuir se não puder ir à igreja?',
      resposta: 'Você pode contribuir através de PIX ou transferência bancária, conforme as informações disponibilizadas nesta página.'
    },
    {
      pergunta: 'Posso direcionar minha oferta para um projeto específico?',
      resposta: 'Sim! Ao fazer sua contribuição, você pode especificar no comprovante ou informar diretamente à equipe financeira para qual projeto ou ministério deseja direcionar sua oferta.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-[#030B2E] via-[#030B2E] to-[#17E6B3]/20 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Heart className="mx-auto mb-6 text-[#17E6B3]" size={64} />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Dízimos e Ofertas
            </h1>
            <p className="text-xl md:text-2xl text-white">
              Contribua com a Obra de Deus
            </p>
            <p className="text-lg text-[#B8C4E3] mt-4">
              Sua contribuição é fundamental para mantermos e expandirmos o trabalho 
              do MEVAM Brasília, alcançando mais vidas para Cristo e impactando nossa cidade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* O que diz a Bíblia */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              O que diz a Bíblia?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {versiculos.map((versiculo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl p-8 border-l-4 border-l-[#17E6B3]"
              >
                <p className="text-[#B8C4E3] italic mb-4 text-lg leading-relaxed">
                  "{versiculo.texto}"
                </p>
                <p className="text-[#17E6B3] font-semibold text-right">
                  - {versiculo.referencia}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formas de Contribuir */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.dContribuiriv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Contribua de Forma Simples e Segura
            </h2>
           
          </motion.dContribuiriv>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* PIX */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 shadow-xl"
            >
              <div className="flex items-center justify-center mb-6">
                <div className="bg-[#030B2E] p-4 rounded-full">
                  <Smartphone className="text-[#17E6B3]" size={48} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-[#030B2E] text-center mb-4">
                PIX
              </h3>
              <p className="text-gray-600 text-center mb-6">
                A forma mais rápida e prática de contribuir com a obra.
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg border border-[#17E6B3]/20">
                  <p className="text-sm text-gray-600 mb-2">Chave PIX (CNPJ):</p>
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-semibold text-[#030B2E]">
                      37.705.916/0001-57
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => copiarPix('37.705.916/0001-57', 'cnpj')}
                      className="p-2 hover:bg-[#17E6B3]/10 rounded-lg transition-colors"
                    >
                      {pixCopiado === 'cnpj' ? (
                        <Check className="text-[#17E6B3]" size={20} />
                      ) : (
                        <Copy className="text-gray-600" size={20} />
                      )}
                    </motion.button>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-[#17E6B3]/20">
                  <p className="text-sm text-gray-600 mb-2">Chave PIX (EMAIL):</p>
                  <div className="flex items-center justify-between">
                    <p className="font-mono font-semibold text-[#030B2E]">
                      pix@mevambrasilia.org
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => copiarPix('pix@mevambrasilia.org', 'email')}
                      className="p-2 hover:bg-[#17E6B3]/10 rounded-lg transition-colors"
                    >
                      {pixCopiado === 'email' ? (
                        <Check className="text-[#17E6B3]" size={20} />
                      ) : (
                        <Copy className="text-gray-600" size={20} />
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[#17E6B3]/10 border-l-4 border-[#17E6B3] rounded">
                <p className="text-sm text-[#030B2E]">
                  💡 Envie o comprovante para nosso WhatsApp para identificarmos sua contribuição.
                </p>
              </div>

              <motion.a
                href="https://wa.me/5561998187484"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-4 w-full bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-6 rounded-xl transition-all text-center block shadow-lg shadow-[#17E6B3]/30"
              >
                <Send className="inline mr-2" size={20} />
                Enviar Comprovante
              </motion.a>
            </motion.div>

            {/* Transferência Bancária - Comentado temporariamente
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl p-8 shadow-xl"
                  >
                    <div className="flex items-center justify-center mb-6">
                    <div className="bg-[#17E6B3] p-4 rounded-full">
                      <CreditCard className="text-[#030B2E]" size={48} />
                    </div>
                    </div>
                    <h3 className="text-2xl font-bold text-[#030B2E] text-center mb-4">
                    Transferência Bancária
                    </h3>
                    <p className="text-gray-600 text-center mb-6">
                    Também aceitamos transferências bancárias
                    </p>

                    <div className="space-y-3">
                    <div className="bg-gray-50 p-3 rounded-lg border border-[#17E6B3]/20">
                      <p className="text-sm text-gray-600">Banco:</p>
                      <p className="font-semibold text-[#030B2E]">Banco do Brasil</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-[#17E6B3]/20">
                      <p className="text-sm text-gray-600">Agência:</p>
                      <p className="font-semibold text-[#030B2E]">0000-0</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-[#17E6B3]/20">
                      <p className="text-sm text-gray-600">Conta Corrente:</p>
                      <p className="font-semibold text-[#030B2E]">00000-0</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-[#17E6B3]/20">
                      <p className="text-sm text-gray-600">CNPJ:</p>
                      <p className="font-semibold text-[#030B2E]">00.000.000/0001-00</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-[#17E6B3]/20">
                      <p className="text-sm text-gray-600">Titular:</p>
                      <p className="font-semibold text-[#030B2E]">MEVAM Brasília</p>
                    </div>
                    </div>
                  </motion.div>
                  */}
                  </div>
                </div>
                </section>

                {/* Prestação de Contas */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <FileText className="text-[#17E6B3] mb-6" size={64} />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prestação de Contas
              </h2>
              <p className="text-[#B8C4E3] text-lg mb-6">
                No MEVAM Brasília, valorizamos a <strong className="text-white">transparência e a integridade</strong> na 
                administração dos recursos. Mensalmente, apresentamos aos membros a prestação 
                de contas detalhada de todas as entradas e saídas.
              </p>
              <div className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl text-white p-6">
                <p className="text-lg font-semibold text-[#17E6B3]">
                  💼 A transparência é um dos nossos valores fundamentais
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                Os recursos são investidos em:
              </h3>
              <div className="space-y-4">
                {investimentos.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-lg hover:bg-white/10 transition-colors border border-[#17E6B3]/20"
                  >
                    <span className="text-3xl">{item.icone}</span>
                    <span className="text-white font-medium">{item.titulo}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perguntas Frequentes */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <HelpCircle className="mx-auto text-[#17E6B3] mb-4" size={64} />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Perguntas Frequentes
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {perguntas.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold text-white mb-3 flex items-start">
                  <Gift className="mr-3 mt-1 flex-shrink-0 text-[#17E6B3]" size={24} />
                  {item.pergunta}
                </h3>
                <p className="text-[#B8C4E3] ml-9">
                  {item.resposta}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-gradient-to-br from-[#030B2E] to-[#17E6B3]/20 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <DollarSign className="mx-auto mb-6 text-[#17E6B3]" size={64} />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Seja um Mantenedor da Obra
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-[#B8C4E3]">
              Sua fidelidade nos dízimos e generosidade nas ofertas são fundamentais 
              para continuarmos avançando e impactando vidas para Cristo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://wa.me/5561998187484"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center shadow-lg shadow-[#17E6B3]/30"
              >
                <Send className="mr-2" size={20} />
                Enviar Comprovante
              </motion.a>
              <motion.a
                href="/contato"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white hover:bg-white hover:text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center"
              >
                Falar com a Tesouraria
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Dizimos;
